import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, LoaderCircle } from 'lucide-react';
import { getAllNotifications } from '../api/api';
import MachineSelector from '../components/MachineSelector';
import HubStatusListModal from '../components/HubStatusListModal';
import useMachinesOverview from '../hooks/useMachinesOverview';
import MapShow from './MapShow';
import styles from './MachineList.module.scss';

function MachineList() {
  const [selectedDevice, setSelectedDevice] = useState('');
  // Company scope filter shared between the machine selector and the map so
  // both show the same subset of machines. Empty array = all companies.
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [statusModal, setStatusModal] = useState(null); // 'inOperation' | 'notInUse' | 'offline'
  const [notifications, setNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 7,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });

  const navigate = useNavigate();

  // Single shared 30-second poll delivering machines, online/in-operation
  // state and incident counts in one request (see useMachinesOverview).
  const overview = useMachinesOverview();
  const overviewMachines = useMemo(() => overview?.machines ?? [], [overview]);

  // serial -> nickname map used to render notification messages
  const machines = useMemo(
    () =>
      overviewMachines.reduce((acc, machine) => {
        acc[machine.serial_number] = machine.nickname;
        return acc;
      }, {}),
    [overviewMachines]
  );

  // Per-hub online/in-operation status. "In operation" follows the same
  // judgment as the Status page: canbus_clients > 0 && gnss_clients > 0 and
  // not battery powered (computed server-side as in_operation).
  const hubStatuses = useMemo(
    () =>
      overviewMachines.map(machine => ({
        id: machine.id,
        serial: machine.serial_number,
        nickname: machine.nickname,
        companyName: machine.company_name,
        isOnline: machine.is_online,
        inOperation: machine.in_operation,
      })),
    [overviewMachines]
  );

  const incidentsData = useMemo(() => {
    const devicesWithIncidents = overviewMachines
      .filter(machine => (machine.untaken_incident_count ?? 0) > 0)
      .map(machine => ({
        machine_id: machine.id,
        serial_number: machine.serial_number,
        nickname: machine.nickname,
        untaken_count: machine.untaken_incident_count,
      }))
      .sort(
        (a, b) =>
          b.untaken_count - a.untaken_count || a.serial_number.localeCompare(b.serial_number)
      );
    return {
      devices_with_incidents: devicesWithIncidents,
      total_devices_with_incidents: devicesWithIncidents.length,
      total_untaken_incidents: devicesWithIncidents.reduce(
        (sum, device) => sum + device.untaken_count,
        0
      ),
    };
  }, [overviewMachines]);

  const incidentsLoading = overview === null;

  // Fetch notifications with pagination
  const fetchNotifications = useCallback(
    async (page = 1) => {
      try {
        const notificationsResponse = await getAllNotifications(page, 7);
        if (notificationsResponse.data && notificationsResponse.data.notifications) {
          const { notifications: rawNotifications, pagination: paginationData } =
            notificationsResponse.data;

          // Convert timestamp to human-readable format
          const formattedNotifications = rawNotifications.map((notification, index) => {
            const timestamp = parseInt(notification.time);
            const now = Math.floor(Date.now() / 1000);
            const diffSeconds = now - timestamp;

            let timeText;
            if (diffSeconds < 60) {
              timeText = `${diffSeconds} sec`;
            } else if (diffSeconds < 3600) {
              timeText = `${Math.floor(diffSeconds / 60)} min`;
            } else if (diffSeconds < 86400) {
              timeText = `${Math.floor(diffSeconds / 3600)} hours`;
            } else if (diffSeconds < 2592000) {
              timeText = `${Math.floor(diffSeconds / 86400)} days`;
            } else {
              timeText = `${Math.floor(diffSeconds / 2592000)} months`;
            }

            // Display machine name in notification if available
            const deviceDisplay = machines[notification.serial_number]
              ? `${machines[notification.serial_number]} (${notification.serial_number})`
              : notification.serial_number;

            return {
              id: (page - 1) * 7 + index + 1,
              message: `Hub ${deviceDisplay} ${notification.text}`,
              time: timeText,
            };
          });

          setNotifications(formattedNotifications);
          setPagination(paginationData);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    },
    [machines]
  );

  // Fetch notifications when page changes or machines are loaded
  useEffect(() => {
    fetchNotifications(currentPage);
  }, [currentPage, machines, fetchNotifications]);

  // Handle page navigation
  const handlePrevPage = () => {
    if (pagination.has_prev) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      setCurrentPage(currentPage + 1);
    }
  };

  // API config
  // const headers = {
  //   'Content-Type': 'application/json',
  //   // Needs to be replaced with a valid API key
  //   'X-API-Key': 'sample-key',
  // };

  const inOperationHubs = hubStatuses.filter(hub => hub.isOnline && hub.inOperation);
  const notInUseHubs = hubStatuses.filter(hub => hub.isOnline && !hub.inOperation);
  const offlineHubList = hubStatuses.filter(hub => !hub.isOnline);

  const statusModalConfig = {
    inOperation: { title: 'Online Hubs (In Operation)', hubs: inOperationHubs },
    notInUse: { title: 'Online Hubs (Not in use)', hubs: notInUseHubs },
    offline: { title: 'Offline Hubs', hubs: offlineHubList },
  };

  // Jump to the hub's Status page, same as clicking its marker on the map
  const handleHubSelect = serial => {
    setStatusModal(null);
    setSelectedDevice(serial);
    navigate(`/device/${serial}/status`);
  };

  const selectorPanel = (
    <div className={styles.hubDeviceSelectorContainer}>
      <MachineSelector
        selectedDevice={selectedDevice}
        setSelectedDevice={setSelectedDevice}
        selectedCompanyIds={selectedCompanyIds}
        setSelectedCompanyIds={setSelectedCompanyIds}
      />
    </div>
  );

  const statusPanels = (
    <div className={styles.statusPanels}>
      <div
        className={`${styles.statusPanel} ${styles.statusPanelClickable}`}
        onClick={() => setStatusModal('inOperation')}
      >
        <h3 className={styles.statusTitle}>
          Online
          <span className={styles.statusTitleSub}>In Operation</span>
        </h3>
        <div className={styles.statusCount}>
          <div className={styles.wifiIcon}>
            <img
              src="/images/wifi_icon.svg"
              alt="Online (In Operation)"
              className={styles.iconImage}
            />
          </div>
          <span className={styles.inOperationCount}>{inOperationHubs.length}</span>
        </div>
      </div>

      <div
        className={`${styles.statusPanel} ${styles.statusPanelClickable}`}
        onClick={() => setStatusModal('notInUse')}
      >
        <h3 className={styles.statusTitle}>
          Online
          <span className={styles.statusTitleSub}>Not in use</span>
        </h3>
        <div className={styles.statusCount}>
          <div className={styles.wifiIcon}>
            <img
              src="/images/wifi_icon_orange.svg"
              alt="Online (Not in use)"
              className={styles.iconImage}
            />
          </div>
          <span className={styles.notInUseCount}>{notInUseHubs.length}</span>
        </div>
      </div>

      <div
        className={`${styles.statusPanel} ${styles.statusPanelClickable}`}
        onClick={() => setStatusModal('offline')}
      >
        <h3 className={styles.statusTitle}>
          Offline
          <span className={styles.statusTitleSub}>Hubs</span>
        </h3>
        <div className={styles.statusCount}>
          <div className={styles.wifiOffIcon}>
            <img src="/images/nowifi_icon.svg" alt="Offline" className={styles.iconImage} />
          </div>
          <span className={styles.offlineCount}>{offlineHubList.length}</span>
        </div>
      </div>
    </div>
  );

  const incidentsPanel = (
    <div className={styles.warningPanel}>
      <h3 className={styles.warningTitle}>Incidents</h3>
      {incidentsLoading ? (
        <div className={styles.loadingMessage}>
          <div className={styles.warningIcon}>
            <LoaderCircle size={22} />
          </div>
          <span className={styles.count}>Loading...</span>
        </div>
      ) : (
        <>
          <div className={styles.warningCount}>
            <div className={styles.warningIcon}>
              {incidentsData.total_devices_with_incidents > 0 ? (
                <AlertTriangle size={22} />
              ) : (
                <CheckCircle2 size={22} />
              )}
            </div>
            <span className={styles.count}>
              {incidentsData.total_devices_with_incidents > 0
                ? `${incidentsData.total_devices_with_incidents} hubs with ${incidentsData.total_untaken_incidents} incident${(incidentsData.total_untaken_incidents ?? 0) === 1 ? '' : 's'}`
                : 'No untaken incidents'}
            </span>
          </div>

          <div className={styles.errorList}>
            {incidentsData.devices_with_incidents.map(device => (
              <div
                key={device.serial_number}
                className={styles.errorItem}
                onClick={() => navigate(`/machine/${device.machine_id}/incidents`)}
              >
                <span>
                  {device.nickname || device.serial_number}
                  {device.nickname && ` (${device.serial_number})`}:
                </span>
                <span className={styles.errorCount}>
                  {`${device.untaken_count ?? 0} incident${(device.untaken_count ?? 0) === 1 ? '' : 's'}`}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const notificationsPanel = (
    <div className={styles.notificationsPanel}>
      <h3 className={styles.notificationsTitle}>Notifications</h3>
      <div className={styles.notificationsList}>
        {notifications.length > 0 ? (
          notifications.map(notification => (
            <div key={notification.id} className={styles.notificationItem}>
              <div className={styles.notificationContent}>
                <div className={styles.notificationDot}>•</div>
                <p className={styles.notificationText}>{notification.message}</p>
              </div>
              <div className={styles.notificationTime}>
                <span>{notification.time}</span>
                <span className={styles.menuDots}>•••</span>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.noNotifications}>No notifications available</div>
        )}
      </div>
      {(pagination.has_prev || pagination.has_next) && (
        <div className={styles.paginationContainer}>
          <button
            className={`${styles.paginationButton} ${!pagination.has_prev ? styles.paginationButtonDisabled : ''}`}
            onClick={handlePrevPage}
            disabled={!pagination.has_prev}
          >
            Last Page
          </button>
          <span className={styles.pageInfo}>
            {pagination.page} / {pagination.total_pages}
          </span>
          <button
            className={`${styles.paginationButton} ${!pagination.has_next ? styles.paginationButtonDisabled : ''}`}
            onClick={handleNextPage}
            disabled={!pagination.has_next}
          >
            Next Page
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.container}>
      <section className={styles.dashboardHeader}>
        <div className={styles.dashboardIntro}>
          <p>Operations workspace</p>
          <h1>Fleet overview</h1>
          <span>Live health, activity and field location across your connected machines.</span>
        </div>
        <div className={styles.fleetBadge}>
          <span className={styles.livePulse} />
          <strong>{overviewMachines.length}</strong>
          <span>machines tracked</span>
        </div>
      </section>

      <div className={styles.workspaceGrid}>
        <aside className={styles.activityRail}>
          <div className={styles.selectorArea}>{selectorPanel}</div>
          <div className={styles.incidentsArea}>{incidentsPanel}</div>
          <div className={styles.notificationsArea}>{notificationsPanel}</div>
        </aside>
        <div className={styles.primaryColumn}>
          <div className={styles.statusArea}>{statusPanels}</div>
          <div className={styles.mapArea}>
            <div className={styles.mapHeader}>
              <div>
                <h2>Map</h2>
                <span>Select a marker to open its machine workspace.</span>
              </div>
              <span className={styles.mapLegend}>
                <i className={styles.legendActive} /> Active
                <i className={styles.legendIdle} /> Idle
                <i className={styles.legendOffline} /> Offline
              </span>
            </div>
            <div className={styles.mapContainer}>
              <MapShow onDeviceSelect={setSelectedDevice} selectedCompanyIds={selectedCompanyIds} />
            </div>
          </div>
        </div>
      </div>

      {statusModal && (
        <HubStatusListModal
          title={statusModalConfig[statusModal].title}
          hubs={statusModalConfig[statusModal].hubs}
          onClose={() => setStatusModal(null)}
          onSelect={handleHubSelect}
        />
      )}
    </div>
  );
}

export default MachineList;
